import { useCallback, useEffect, useRef, useState } from "react";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import {
  ThreadId,
  EnvironmentId,
  type CmmdProjectTaskDraft,
  type CmmdProjectTaskDraftPreview,
  type CmmdProjectTaskDraftProposal,
} from "@cmmd-forge/contracts";

import { CmmdApiError, cmmdApiClient } from "~/environments/cmmdApi";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { useProjectLeadTaskCreationThread } from "~/hooks/useProjectLeadTaskCreationThread";
import { commitTaskDraftWithReconciliation } from "./commitTaskDraft";
import {
  ensureRestoredDraftThread,
  errorMessage,
  isCurrentRestore,
  ProjectLeadDraftThreadRestoreError,
  removeStoredDraft,
  restoredThreadRef,
  storedDraftPointer,
} from "./useProjectLeadTaskDraft.logic";
import {
  applyCreatedDraft,
  applyReservedExistingDraft,
  assertCurrentRequest,
  createDraftWithIdempotencyRecovery,
  discardError,
  draftCreateAttempt,
  isActiveDraftReservation,
  readActiveDraftReservation,
  readCreateAttempt,
  removeCreateAttempt,
  requestWasSuperseded,
  resolveCommitAttempt,
  retireCommitAttemptForVersion,
  retireDefinitiveCreateAttempt,
  retireSupersededCreateAttempt,
  settleCurrentRequest,
  startOwnerId,
  withCreateAttemptReservation,
  writeCreateAttempt,
  type DraftCommitAttempt,
  type DraftCreateAttempt,
} from "./useProjectLeadTaskDraft.attempts.logic";

import type { ProjectLeadTaskDraftStartInput } from "./useProjectLeadTaskDraft.logic";
import type {
  ProjectLeadTaskDraftController,
  UseProjectLeadTaskDraftInput,
} from "./useProjectLeadTaskDraft.types";

export type {
  ProjectLeadTaskDraftController,
  UseProjectLeadTaskDraftInput,
} from "./useProjectLeadTaskDraft.types";

export function useProjectLeadTaskDraft(
  input: UseProjectLeadTaskDraftInput,
): ProjectLeadTaskDraftController {
  const { projectId, projectRef, onThreadReady, onCommitted, onDiscarded } = input;
  const { ensureThread } = useProjectLeadTaskCreationThread();
  const ownerId = useCmmdWorkIntakeSnapshot().identity?.id ?? null;
  const [draft, setDraft] = useState<CmmdProjectTaskDraft | null>(null);
  const [proposal, setProposal] = useState<CmmdProjectTaskDraftProposal | null>(null);
  const [preview, setPreview] = useState<CmmdProjectTaskDraftPreview | null>(null);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(
    () => ownerId === null || storedDraftPointer(ownerId, input.projectId) !== null,
  );
  const [pendingCreate, setPendingCreate] = useState(
    () => readCreateAttempt(ownerId, input.projectId) !== null,
  );
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committedTaskIds, setCommittedTaskIds] = useState<readonly number[]>([]);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const commitAttemptRef = useRef<DraftCommitAttempt | null>(null);
  const projectIdentity = `${input.projectId}:${input.projectRef?.environmentId ?? "none"}:${input.projectRef?.projectId ?? "none"}`;
  const previousIdentityRef = useRef(`${ownerId ?? "none"}:${projectIdentity}`);
  const requestGenerationRef = useRef(0);
  const currentIdentity = `${ownerId ?? "none"}:${projectIdentity}`;
  if (previousIdentityRef.current !== currentIdentity) requestGenerationRef.current += 1;
  const createAttemptRef = useRef<DraftCreateAttempt | null>(
    readCreateAttempt(ownerId, input.projectId),
  );
  const notifiedCommittedDraftIdRef = useRef<number | null>(null);
  const notifiedDiscardedDraftIdRef = useRef<number | null>(null);
  const onThreadReadyRef = useRef(onThreadReady);
  const onCommittedRef = useRef(onCommitted);
  const onDiscardedRef = useRef(onDiscarded);

  useEffect(() => {
    onThreadReadyRef.current = onThreadReady;
    onCommittedRef.current = onCommitted;
    onDiscardedRef.current = onDiscarded;
  }, [onThreadReady, onCommitted, onDiscarded]);

  const retireCurrentCreateAttempt = useCallback(() => {
    retireSupersededCreateAttempt({
      attempt: createAttemptRef.current,
      ownerId,
      projectId,
      clearAttempt: () => {
        createAttemptRef.current = null;
        setPendingCreate(false);
      },
    });
  }, [projectId, ownerId]);

  const applyCommittedDraft = useCallback(
    (committed: CmmdProjectTaskDraft) => {
      retireCurrentCreateAttempt();
      setDraft(committed);
      setProposal(null);
      setSelectedRef(null);
      setCommittedTaskIds(committed.committedTaskIds);
      setDirty(false);
      setStale(false);
      setPreview(null);
      if (notifiedCommittedDraftIdRef.current !== committed.id) {
        notifiedCommittedDraftIdRef.current = committed.id;
        onCommittedRef.current?.(committed.committedTaskIds);
      }
      if (ownerId !== null) removeStoredDraft(ownerId, projectId, committed.id);
    },
    [ownerId, projectId, retireCurrentCreateAttempt],
  );

  const applyDiscardedDraft = useCallback(
    (discardedDraftId: number) => {
      retireCurrentCreateAttempt();
      if (ownerId !== null) removeStoredDraft(ownerId, projectId, discardedDraftId);
      setDraft(null);
      setProposal(null);
      setPreview(null);
      setSelectedRef(null);
      setDirty(false);
      setStale(false);
      setCommittedTaskIds([]);
      setError(null);
      if (notifiedDiscardedDraftIdRef.current !== discardedDraftId) {
        notifiedDiscardedDraftIdRef.current = discardedDraftId;
        onDiscardedRef.current?.();
      }
    },
    [ownerId, projectId, retireCurrentCreateAttempt],
  );

  useEffect(() => {
    if (previousIdentityRef.current !== currentIdentity) {
      previousIdentityRef.current = currentIdentity;
      setDraft(null);
      setProposal(null);
      setPreview(null);
      setSelectedRef(null);
      setDirty(false);
      setStale(false);
      setError(null);
      setCommittedTaskIds([]);
      commitAttemptRef.current = null;
      createAttemptRef.current = readCreateAttempt(ownerId, projectId);
      setPendingCreate(createAttemptRef.current !== null);
      setBusy(false);
    }
    if (ownerId === null) {
      setRestoring(true);
      return;
    }
    const storedPointer = storedDraftPointer(ownerId, projectId);
    if (storedPointer === null) {
      setRestoring(false);
      return;
    }
    setRestoring(true);
    let cancelled = false;
    const requestGeneration = requestGenerationRef.current;
    void cmmdApiClient
      .getProjectTaskDraft(String(projectId), storedPointer.draftId)
      .then(async (restored) => {
        if (!isCurrentRestore(cancelled, requestGenerationRef, requestGeneration)) return;
        if (restored.status === "draft") {
          retireSupersededCreateAttempt({
            attempt: createAttemptRef.current,
            ownerId,
            projectId,
            clearAttempt: () => {
              createAttemptRef.current = null;
              setPendingCreate(false);
            },
          });
          const ensuredThreadRef = await ensureRestoredDraftThread({
            draft: restored,
            projectId,
            projectRef,
            storedEnvironmentId: storedPointer.environmentId,
            ensureThread,
          });
          if (!isCurrentRestore(cancelled, requestGenerationRef, requestGeneration)) return;
          setDraft(restored);
          setProposal(restored.proposal);
          setSelectedRef(restored.proposal?.tasks[0]?.localRef ?? null);
          setRestoring(false);
          setError(null);
          onThreadReadyRef.current?.(ensuredThreadRef);
        } else if (restored.status === "committed") {
          applyCommittedDraft(restored);
          setRestoring(false);
        } else if (restored.status === "discarded") {
          applyDiscardedDraft(storedPointer.draftId);
          setRestoring(false);
        }
      })
      .catch((cause: unknown) => {
        if (!isCurrentRestore(cancelled, requestGenerationRef, requestGeneration)) return;
        if (
          !(cause instanceof ProjectLeadDraftThreadRestoreError) &&
          cause instanceof CmmdApiError &&
          cause.status === 404
        ) {
          removeStoredDraft(ownerId, projectId, storedPointer.draftId);
          setRestoring(false);
          return;
        }
        if (!cancelled) {
          setError("Forge could not restore your saved Project Lead draft. Retry to continue.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    applyCommittedDraft,
    applyDiscardedDraft,
    currentIdentity,
    ensureThread,
    projectId,
    projectRef,
    ownerId,
    restoreAttempt,
  ]);

  const persistProposal = async (): Promise<CmmdProjectTaskDraft | null> => {
    if (draft === null || proposal === null) return null;
    const requestGeneration = requestGenerationRef.current;
    try {
      const updated = await cmmdApiClient.updateProjectTaskDraft(
        String(input.projectId),
        draft.id,
        {
          expectedVersion: draft.version,
          mutation: { kind: "replace_proposal", proposal },
        },
      );
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      setDraft(updated);
      retireCommitAttemptForVersion(commitAttemptRef, updated.version);
      setProposal(updated.proposal);
      setDirty(false);
      setStale(false);
      setPreview(null);
      return updated;
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return null;
      const conflict = cause instanceof CmmdApiError && cause.status === 409;
      setStale(conflict);
      setError(
        conflict
          ? "Project Lead changed this draft. Reload the latest version before saving again."
          : errorMessage(cause, "Forge could not save this task draft."),
      );
      return null;
    }
  };

  const start = async (startInput: ProjectLeadTaskDraftStartInput) => {
    const activeOwnerId = startOwnerId(restoring, busy, ownerId);
    if (activeOwnerId === null) return;
    if (input.projectRef === null) {
      setError("Connect this CMMD project to a Forge project before starting Project Lead.");
      return;
    }
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      const reservation = await withCreateAttemptReservation(
        activeOwnerId,
        input.projectId,
        async () => {
          const activeReservation = await readActiveDraftReservation(
            activeOwnerId,
            input.projectId,
          );
          if (activeReservation !== null) return activeReservation;
          let reservedAttempt =
            readCreateAttempt(activeOwnerId, input.projectId) ??
            createAttemptRef.current ??
            draftCreateAttempt(null, input.projectId, startInput);
          writeCreateAttempt(activeOwnerId, input.projectId, reservedAttempt);
          assertCurrentRequest(requestGenerationRef, requestGeneration);
          createAttemptRef.current = reservedAttempt;
          setPendingCreate(true);
          if (
            reservedAttempt.environmentId !== undefined &&
            reservedAttempt.forgeThreadId !== undefined
          ) {
            return {
              attempt: reservedAttempt,
              threadRef: scopeThreadRef(
                EnvironmentId.make(reservedAttempt.environmentId),
                ThreadId.make(reservedAttempt.forgeThreadId),
              ),
            } as const;
          }
          const ensured = await ensureThread({
            projectRef: input.projectRef!,
            cmmdProjectId: String(input.projectId),
            taskDraftId: "pending",
            contextPrompt: `Help refine this task request: ${reservedAttempt.proposal.goal}`,
          });
          reservedAttempt = {
            ...reservedAttempt,
            environmentId: ensured.threadRef.environmentId,
            forgeThreadId: ensured.threadRef.threadId,
          };
          writeCreateAttempt(activeOwnerId, input.projectId, reservedAttempt);
          return { attempt: reservedAttempt, threadRef: ensured.threadRef } as const;
        },
      );
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      if (isActiveDraftReservation(reservation)) {
        applyReservedExistingDraft({
          reservation,
          applyCommitted: applyCommittedDraft,
          applyDiscarded: applyDiscardedDraft,
          applyActive: (existing, pointer) => {
            retireCurrentCreateAttempt();
            setDraft(existing);
            setProposal(existing.proposal);
            setSelectedRef(existing.proposal?.tasks[0]?.localRef ?? null);
            const restoredRef = restoredThreadRef(
              input.projectRef,
              existing,
              pointer.environmentId,
            );
            if (restoredRef !== null) input.onThreadReady?.(restoredRef);
          },
        });
        return;
      }
      let attempt = reservation.attempt;
      createAttemptRef.current = attempt;
      setPendingCreate(true);
      const created = await createDraftWithIdempotencyRecovery({
        projectId: input.projectId,
        attempt,
        threadId: reservation.threadRef.threadId,
        onRotate: (rotatedAttempt) => {
          removeCreateAttempt(activeOwnerId, input.projectId, attempt.idempotencyKey);
          writeCreateAttempt(activeOwnerId, input.projectId, rotatedAttempt);
          createAttemptRef.current = rotatedAttempt;
          attempt = rotatedAttempt;
        },
      });
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      await ensureThread({
        projectRef: { ...input.projectRef, environmentId: reservation.threadRef.environmentId },
        cmmdProjectId: String(input.projectId),
        taskDraftId: String(created.id),
        contextPrompt: `Review and improve task draft ${created.id}: ${attempt.proposal.goal}`,
      });
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      const pointerStored = applyCreatedDraft({
        created,
        ownerId: activeOwnerId,
        projectId: input.projectId,
        setDraft,
        setProposal,
        setSelectedRef,
        createIdempotencyKey: attempt.idempotencyKey,
        environmentId: reservation.threadRef.environmentId,
      });
      if (pointerStored) createAttemptRef.current = null;
      setPendingCreate(false);
      setDirty(false);
      setStale(false);
      setPreview(null);
      input.onThreadReady?.(reservation.threadRef);
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return;
      if (
        retireDefinitiveCreateAttempt({
          cause,
          ownerId,
          projectId: input.projectId,
          attempt: createAttemptRef.current,
        })
      ) {
        createAttemptRef.current = null;
        setPendingCreate(false);
      }
      setError(errorMessage(cause, "Forge could not start guided task creation."));
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const reload = async () => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      const latest = await cmmdApiClient.getProjectTaskDraft(String(input.projectId), draft.id);
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      if (latest.status === "committed") {
        applyCommittedDraft(latest);
        return;
      }
      if (latest.status === "discarded") {
        applyDiscardedDraft(latest.id);
        return;
      }
      setDraft(latest);
      retireCommitAttemptForVersion(commitAttemptRef, latest.version);
      setProposal(latest.proposal);
      setSelectedRef((current) =>
        latest.proposal?.tasks.some((task) => task.localRef === current)
          ? current
          : (latest.proposal?.tasks[0]?.localRef ?? null),
      );
      setDirty(false);
      setStale(false);
      setPreview(null);
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return;
      setError(errorMessage(cause, "Forge could not reload the latest task draft."));
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      return (await persistProposal()) !== null;
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const validateDraft = async (): Promise<CmmdProjectTaskDraftPreview | null> => {
    if (draft === null || proposal === null) return null;
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      const currentDraft = dirty ? await persistProposal() : draft;
      if (currentDraft === null) return null;
      const result = await cmmdApiClient.previewProjectTaskDraft(
        String(input.projectId),
        currentDraft.id,
        { expectedVersion: currentDraft.version },
      );
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      setDraft(currentDraft);
      retireCommitAttemptForVersion(commitAttemptRef, result.version);
      setProposal(currentDraft.proposal);
      setPreview(result);
      return result;
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return null;
      const conflict = cause instanceof CmmdApiError && cause.status === 409;
      setStale(conflict);
      setError(
        conflict
          ? "This validation is stale. Reload the latest Project Lead draft and try again."
          : errorMessage(cause, "The task tree is not ready to create."),
      );
      return null;
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const validate = async (): Promise<boolean> => (await validateDraft()) !== null;

  const resolveSuggestion = async (suggestionId: string, resolution: "apply" | "dismiss") => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      const currentDraft = dirty ? await persistProposal() : draft;
      if (currentDraft === null) return;
      const updated = await cmmdApiClient.updateProjectTaskDraft(
        String(input.projectId),
        currentDraft.id,
        {
          expectedVersion: currentDraft.version,
          mutation: { kind: "resolve_suggestion", suggestionId, resolution },
        },
      );
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      setDraft(updated);
      retireCommitAttemptForVersion(commitAttemptRef, updated.version);
      setProposal(updated.proposal);
      setDirty(false);
      setStale(false);
      setPreview(null);
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return;
      const conflict = cause instanceof CmmdApiError && cause.status === 409;
      setStale(conflict);
      setError(
        conflict
          ? "That suggestion changed. Reload the draft before resolving it."
          : errorMessage(cause, "Forge could not resolve that suggestion."),
      );
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const discard = async (): Promise<boolean> => {
    const activeDraft = draft?.status === "draft" ? draft : null;
    if (activeDraft === null || busy) return false;
    setBusy(true);
    setError(null);
    const requestGeneration = requestGenerationRef.current;
    try {
      const discarded = await cmmdApiClient.discardProjectTaskDraft(
        String(input.projectId),
        activeDraft.id,
        { expectedVersion: activeDraft.version },
      );
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      applyDiscardedDraft(discarded.id);
      commitAttemptRef.current = null;
      retireCurrentCreateAttempt();
      return true;
    } catch (cause: unknown) {
      if (requestWasSuperseded(requestGenerationRef, requestGeneration, cause)) return false;
      const failure = discardError(cause);
      setStale(failure.stale);
      setError(failure.message);
      return false;
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  const commit = async (): Promise<boolean> => {
    if (draft === null) return false;
    const attempt = await resolveCommitAttempt(commitAttemptRef.current, validateDraft);
    if (attempt === null) return false;
    setBusy(true);
    setError(null);
    commitAttemptRef.current = attempt;
    const requestGeneration = requestGenerationRef.current;
    try {
      const result = await commitTaskDraftWithReconciliation({
        projectId: String(input.projectId),
        draftId: draft.id,
        expectedVersion: attempt.expectedVersion,
        idempotencyKey: attempt.idempotencyKey,
      });
      assertCurrentRequest(requestGenerationRef, requestGeneration);
      if (result.kind === "failed") {
        if (result.stale) commitAttemptRef.current = null;
        setStale(result.stale);
        setError(
          result.stale
            ? "The draft changed before creation. Reload and review it again."
            : errorMessage(result.cause, "Forge could not create the task tree."),
        );
        return false;
      }
      applyCommittedDraft(result.draft);
      commitAttemptRef.current = null;
      return true;
    } finally {
      settleCurrentRequest(requestGenerationRef, requestGeneration, () => setBusy(false));
    }
  };

  return {
    draft,
    proposal,
    preview,
    selectedRef,
    restoring,
    pendingCreate,
    busy,
    dirty,
    stale,
    error,
    committedTaskIds,
    retryRestore: () => {
      setError(null);
      setRestoreAttempt((current) => current + 1);
    },
    start,
    select: setSelectedRef,
    edit: (nextProposal) => {
      commitAttemptRef.current = null;
      setProposal(nextProposal);
      setDirty(true);
      setPreview(null);
    },
    save,
    reload,
    validate,
    resolveSuggestion,
    discard,
    commit,
    reset: () => {
      setDraft(null);
      setProposal(null);
      setPreview(null);
      setSelectedRef(null);
      setDirty(false);
      setStale(false);
      setError(null);
      setCommittedTaskIds([]);
      commitAttemptRef.current = null;
      createAttemptRef.current = null;
      setPendingCreate(false);
    },
  };
}
